import type { ReactNode } from "react";
import { Box } from "../../../components/ui/box";
import { usePrefersReducedMotion } from "../../../hooks/use-prefers-reduced-motion";

/**
 * Shared plumbing for the finished-chart layer (the recharts engine). Internal
 * only: recharts never crosses the public surface — consumers see semantic
 * props and `ChartColorToken`, and everything here paints live `var()` strings
 * (see ../colors.ts), so a theme flip re-resolves in CSS with no re-render.
 */

/** Axis tick text props every finished chart shares (quiet, on-token). */
export const AXIS_TICK = {
  fill: "var(--muted-foreground)",
  fontSize: 12,
} as const;

/** The soft wash a bar chart paints behind the hovered category. */
export const HOVER_CURSOR_FILL =
  "color-mix(in oklab, var(--foreground) 5%, transparent)";

/**
 * Hover-card motion for a chart whose card lands on DISCRETE positions — a
 * category band, a time bucket. The card travels between them instead of
 * teleporting, so sweeping across the plot reads as one object moving rather
 * than a card blinking out and back in somewhere else. Spread it beside
 * `isAnimationActive` on those tooltips. A card that already tracks the
 * pointer continuously (the pie's) leaves it off — a transition there only
 * makes the card trail the cursor.
 */
export const TOOLTIP_MOTION = {
  animationDuration: 150,
  animationEasing: "ease-out",
} as const;

/**
 * The same travel for the marks that point AT the hovered position, so the
 * card, the dot and the cursor arrive together: the active dot's `cx`/`cy` and
 * the cursor shape's `d` (recharts draws every cursor — guide line and band
 * wash alike — as a `<path>`). Those are SVG geometry properties, so CSS
 * transitions them like any other property; where a browser has not
 * implemented one as a property the mark simply snaps, which is the behavior
 * without the class. This motion is CSS-driven, so it carries its own
 * `motion-reduce:` opt-out rather than a `useChartMotion()` gate.
 */
export const ACTIVE_DOT_TRAVEL_CLASS =
  "transition-[cx,cy] duration-150 ease-out motion-reduce:transition-none";
export const HOVER_CURSOR_TRAVEL_CLASS =
  "transition-[d] duration-150 ease-out motion-reduce:transition-none";

/** Whether chart entrance/update animation should run. */
export function useChartMotion(): boolean {
  return !usePrefersReducedMotion();
}

/**
 * Entrance-motion timing every animated mark shares. The engine's defaults are
 * a leisurely 1500ms ease — and Pie waits a further 400ms before starting — so
 * a dashboard mounting many charts at once reads as loading long after the
 * data arrived. Spread this beside `isAnimationActive` on every animated mark.
 */
export const CHART_MOTION = {
  animationBegin: 0,
  animationDuration: 600,
  animationEasing: "ease-out",
} as const;

/**
 * The popover-chrome shell a finished chart's hover card renders into — the
 * same surface recipe as `ChartTooltipCard`, minus the positioning (recharts
 * owns tooltip placement).
 */
export function TooltipShell({ children }: { children: ReactNode }) {
  return (
    <Box surface="popover" rounded="lg" border paddingX="sm" paddingY="xs">
      {children}
    </Box>
  );
}
